import React, {useEffect, useState} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from '@/styles/Layout.module.css';
import type {Category, WebsiteInfo} from '@/api/api';
import {getCategoryList, getFileUrl, getWebsiteInfo} from '@/api/api';
import body_bg from '@/assist/body_bg.jpg';

interface LayoutProps {
    children: React.ReactNode;
}

export default function Layout({children}: LayoutProps) {
    const [websiteInfo, setWebsiteInfo] = useState<WebsiteInfo | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [categories, setCategories] = useState<{ [key: string]: string[] }>({});
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname;

    useEffect(() => {
        // Fetch website info and categories on component mount
        const fetchData = async () => {
            try {
                // Fetch website info
                const websiteResponse = await getWebsiteInfo();
                if (websiteResponse.data.code === 1) {
                    setWebsiteInfo(websiteResponse.data.data);
                    
                    // Set favicon dynamically based on icon field
                    if (websiteResponse.data.data.icon) {
                        const iconUrl = getFileUrl(websiteResponse.data.data.icon);
                        const linkElement = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
                        
                        if (linkElement) {
                            // Update existing favicon
                            linkElement.href = iconUrl;
                        } else {
                            // Create a new favicon link element if it doesn't exist
                            const newLink = document.createElement('link');
                            newLink.rel = 'icon';
                            newLink.href = iconUrl;
                            document.head.appendChild(newLink);
                        }
                    }
                }

                // Fetch categories for navigation
                const categoriesResponse = await getCategoryList();
                if (categoriesResponse.data.code === 1) {
                    const categoriesData = categoriesResponse.data.data;
                    const categoriesMap: { [key: string]: string[] } = {};

                    categoriesData.forEach((category: Category) => {
                        categoriesMap[category.name] = category.labelList || [];
                    });

                    setCategories(categoriesMap);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
        }
    };

    return (
        <>
            {/* Header Start */}
            <div className={styles.header}>
                <div className={styles.headerContainer} style={{ backgroundImage: `url(${body_bg})` }}>
                    <div className={styles.logo}>
                        <Link to="/" title={websiteInfo?.title || "NATF标准化职业技能考试认证"}>
                            {websiteInfo?.logPic ? (
                                <img
                                    src={getFileUrl(websiteInfo.logPic)}
                                    alt={websiteInfo.title}
                                    width="600px"
                                />
                            ) : (
                                <img src="#" alt="NATF标准化职业技能考试认证"
                                     width="600px"/>
                            )}
                        </Link>
                    </div>
                    {/* Search Bar Start */}
                    <div className={styles.search}>
                        <form onSubmit={handleSearch}>
                            <input
                                className={styles.iptSea}
                                type="text"
                                placeholder="请输入关键字"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="搜索关键词"
                            />
                            <button className={styles.searchBtn} type="submit">搜索</button>
                        </form>
                    </div>
                    {/* Search Bar End */}
                </div>
            </div>
            {/* Header End */}

            {/* Navigation Start */}
            <div className={styles.nav}>
                <ul id="navul" className={styles.navul}>
                    <li>
                        <Link to="/" className={pathname === '/' ? styles.active : ''}>网站首页</Link>
                    </li>
                    <li>
                        <Link to="/about" className={pathname === '/about' ? styles.active : ''}
                              title="关于我们">关于我们</Link>
                    </li>
                    <li>
                        <Link to="/research" className={pathname.startsWith('/research') ? styles.active : ''}
                              title="研究动态">研究动态</Link>
                        <div className={styles.subnav}>
                            <Link to="/research/policy"
                                  className={pathname === '/research/policy' ? styles.active : ''}>政策解读</Link>
                            <Link to="/research/education"
                                  className={pathname === '/research/education' ? styles.active : ''}>教育动态</Link>
                        </div>
                    </li>
                    <li>
                        <Link to="/news" className={pathname.startsWith('/news') ? styles.active : ''}
                              title="新闻中心">新闻中心</Link>
                        <div className={styles.subnav}>
                            <Link to="/news/notices"
                                  className={pathname === '/news/notices' ? styles.active : ''}>通知公告</Link>
                            <Link to="/news/decisions"
                                  className={pathname === '/news/decisions' ? styles.active : ''}>决策参考</Link>
                            <Link to="/news/research"
                                  className={pathname === '/news/research' ? styles.active : ''}>调查研究</Link>
                        </div>
                    </li>
                    <li>
                        <Link to="/policies" className={pathname.startsWith('/policies') ? styles.active : ''}
                              title="政策法规">政策法规</Link>
                    </li>
                    <li>
                        <Link to="/skills" className={pathname.startsWith('/skills') ? styles.active : ''}
                              title="职业技能">职业技能</Link>
                    </li>
                    <li>
                        <Link to="/certificates" className={pathname.startsWith('/certificates') ? styles.active : ''}
                              title="证书查询">证书查询</Link>
                    </li>
                    <li>
                        <Link to="/contact" className={pathname === '/contact' ? styles.active : ''}
                              title="联系我们">联系我们</Link>
                    </li>
                </ul>
                <div className={styles.clearfix}></div>
            </div>
            {/* Navigation End */}

            {/* Main Content */}
            <div className={styles.container}>
                {children}
            </div>

            {/* Footer Start */}
            <div className={styles.footerContainer}>
                {websiteInfo ? (
                    <>
                        <p>{websiteInfo.copyright}</p>
                        <p><a href="//beian.miit.gov.cn">{websiteInfo.registrationNumber}</a></p>
                        <p>{websiteInfo.address}</p>
                    </>
                ) : (
                    <>
                    </>
                )}
            </div>
            {/* Footer End */}
        </>
    );
}
